import React, { useEffect, useState } from "react";
import styles from "./index.module.css";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useHistory, useParams } from "react-router-dom";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import {
  BookingStatus,
  IInvoice,
  IInvoiceItem,
  IInvoiceItemType,
  PaymentGatewayLabels,
} from "../../../../reducers/bookings/types";
import { StyledTableCell, StyledTableRow } from "../../../StyledTable";
import { DATE_TYPE, formatGraphQLErrorMessage, getTaxDescription, getVehicleGroupApplicableTaxesDetails, toCurrency } from "../../../utils";
import { DateTime as d, Settings } from "luxon";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { GET_INVOICE } from "../../../../graphql/invoices/getInvoiceQuery";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import DownloadPdf from "../../../Pdf/DownloadPdf";
import { InvoiceStatus, InvoiceType } from "../../NewBooking/Invoices/utils";
import InvoicePaymentDialog from './PaymentDialog';
import { getLocalizedBookingSyntex, getLocalizedDateFormat, getLocalizedTaxSyntex } from "../../../../utils/localized.syntex";
import { ADD_BOOKING_PO_NUMBER } from "../../../../graphql/bookings/addBookingPoNumberMutation";
import { socket } from "../../../../utils/socket";
import { useTranslation } from "react-i18next";
export interface IDamageList {
  number: number;
  damageKind: string;
  image: string;
}

const Invoice = () => {
  const history = useHistory();
  const snackbar = useSnackBar();
  const { id: invoiceId } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<IInvoice>();
  const [poNumber, setPoNumber] = useState<string>("");
  const [isEditablePONumber, setIsEditablePONumber] = useState<boolean>(false);
  const website = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const { currency, locale } = website.organisation;
  const { country } = website.organisation.address;
  const authUser = useSelector((state: IAppState) => state.authReducer.authUser);
  const [hideDetails, setHideDetails] = useState<boolean>(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false);
  const {t} = useTranslation();
  const [getInvoice, { loading: bookingLoading, data: invoiceData }] =
    useLazyQuery(GET_INVOICE, {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (!data.consumerInvoice) {
          history.push("/account/invoices");
        }
      },
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR
        });
      }
    });

  const [addBookingPoNumber] = useMutation(ADD_BOOKING_PO_NUMBER, {
    onCompleted: (data) => {
      snackbar({
        message: `${getLocalizedBookingSyntex(country)} ${t("root:purchaseOrderUpdated")}`,
        variant: SnackBarVariant.SUCCESS
      })
      if (invoice && invoice.booking?.id) {
        setInvoice({
          ...invoice,
          booking: {
            ...invoice?.booking,
            poNumber: data?.addBookingPoNumber || ""
          }
        })
      }
      setPoNumber(data.addBookingPoNumber)
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  useEffect(() => {
    if (invoiceId) {
      getInvoice({
        variables: {
          id: invoiceId,
        },
      });
    }
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceData && invoiceData.consumerInvoice) {
      const _invoice: IInvoice = invoiceData.consumerInvoice;
      setInvoice(_invoice);
      setPoNumber(_invoice.booking?.poNumber || "")
      setHideDetails([BookingStatus.REQUIRES_APPROVAL, BookingStatus.CONFIRMED].includes(_invoice.booking?.status));
      Settings.defaultZone = invoice?.booking?.branch?.timeZone || "Europe/London"
      if (invoiceData.consumerInvoice?.adjustedDeposit?.amount && invoiceData.consumerInvoice.booking.depositDetails?.payment) {
        const payments: any[] = [...invoiceData.consumerInvoice.payments];
        if (!payments.some((payment) => payment.id === invoiceData.consumerInvoice.booking.depositDetails?.payment.id)) {
          payments.push({
            ...invoiceData.consumerInvoice.booking.depositDetails?.payment,
            amount: invoiceData.consumerInvoice.adjustedDeposit.amount,
            dateCreated: invoiceData.consumerInvoice.adjustedDeposit.adjustedAt,
            paymentMode: "Paid by Deposit",
            refundAmount: 0,
          })
          setInvoice({
            ...invoiceData.consumerInvoice,
            payments
          })
        }
      }
    }
    return () => {
      Settings.defaultZone = "Europe/London"
    }
  }, [invoiceData]);

  if (!invoice || !invoiceData) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress size={30} thickness={5} />
      </div>
    );
  }

  const registrationNumbers: string[] = [];
  invoice.booking?.currentBookingSchedules.forEach((schedule) => {
    if (schedule.vehicle) {
      registrationNumbers.push(schedule.vehicle.isGhostVehicle ? t("root:toBeAssigned") : schedule.vehicle.licencePlate);
    }
  });

  const columns = [
    "#",
    "Particulars",
    "Quantity",
    "Unit Price",
    "Duration",
    "Discounted Price",
    "VAT/TAX",
    "Amount",
  ];
  const paymentColumns = ["Date", "Amount", "Mode Of Payments", "Status"];

  const consolidatedInvoicesColumns = ["#", "Invoice Reference Number", "Vehicle Registration Numbers"];

  const getRateTypeString = (rateType: string) => {
    if (rateType) {
      switch (rateType) {
        case "hourly":
          return "Hour (s)";
        case "daily":
          return "Day (s)";
        case "weekly":
          return "Week (s)";
        case "monthly":
          return "Month (s)";
        default:
          return "Day (s)";
      }
    }
  };

  const addPoNumberToBooking = (_poNumber: string) => {
    if (invoice.booking && invoice.booking.id) {
      addBookingPoNumber({
        variables: {
          bookingId: invoice.booking.id,
          poNumber: _poNumber
        }
      })
    }
  }

  return (
    <div className={styles.column}>
      <div className="flex cross-center">
        {paymentDialogOpen && (
          <InvoicePaymentDialog
            open={paymentDialogOpen}
            setOpen={(open: boolean) => setPaymentDialogOpen(open)}
            invoice={invoice}
            bookingId={invoice.booking?.id}
            onConfirmation={(invoice) => {

            }}
            returnUrl={`account/invoices/${invoice.id}`}
            getUpdatedInvoice={() => {
              getInvoice({
                variables: {
                  id: invoiceId,
                },
              });
            }}
          />
        )}
        <Typography
          variant="h4"
          className="semi-bold flex cross-center space-between"
        >
          <IconButton
            size="small"
            edge="start"
            onClick={() => history.goBack()}
          >
            <ArrowBackIcon />
          </IconButton>
          <span className="margin-left">{t("root:invoiceSummary")}</span>
        </Typography>
        <div className="flex fill main-end">
          <DownloadPdf
            getSource={{ type: "INVOICE", id: invoice.id }}
            pdfSource={invoice.invoiceUrl}
            fileName={`${invoice.invoiceType === InvoiceType.SUNDRY ? t("root:sundry") : ""
              }${t("root:invoice")}-${invoice?.invoiceRef}.pdf`}
          />
          {invoice.dueAmount ? <>
            <span className="padding-right"></span>
            <Button
              onClick={() =>
                setPaymentDialogOpen(true)
              }
              variant="contained"
              color="primary"
              size="large"
              disabled={invoice.booking?.status === BookingStatus.REQUIRES_APPROVAL}
              style={{
                background: "var(--primary-color)",
                textTransform: "initial",
                boxShadow: "none",
                fontSize: 14,
                fontWeight: 500,
                color: invoice.booking?.status === BookingStatus.REQUIRES_APPROVAL ? "rgba(255,255,255,0.3)"
                  : "primary",
              }}
            >
              {t("root:addPayment")}
            </Button></> : null}
        </div>
      </div>
      <Grid container>
        <Grid item xs={12} md={4} style={{ display: "flex" }}>
          <div className={styles.card}>
            {invoice.businessCustomer && <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:businessName")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {invoice.businessCustomer.businessName}
              </Typography>
            </div>}
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:customerName")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {invoice.businessCustomer ? invoice.businessCustomer.contact.firstName : invoice.customer.firstName}{" "}
                {invoice.businessCustomer ? invoice.businessCustomer.contact.lastName : invoice.customer.lastName}
              </Typography>
            </div>
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:customerEmail")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {invoice.businessCustomer ? invoice.businessCustomer.billing.email : invoice.customer.email}
              </Typography>
            </div>
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:customerPhoneNumber")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {invoice.businessCustomer ? invoice.businessCustomer.billing.phoneNumber.phone : invoice.customer.phoneNumber.phone}
              </Typography>
            </div>
            {invoice.businessCustomer && <div>
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:address")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {invoice.businessCustomer ? invoice.businessCustomer.registeredAddress.fullAddress : invoice.customer.address}
              </Typography>
            </div>}
          </div>
        </Grid>
        <Grid item xs={12} md={3} style={{ display: "flex" }}>
          <div className={styles.card}>
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:invoiceReference")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {invoice.invoiceRef}
              </Typography>
            </div>
            {invoice.invoiceType !== InvoiceType.CONSOLIDATED && (
              <>
                <div className="margin-bottom">
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {`${getLocalizedBookingSyntex(country)} ${t("root:reference")}:`}
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {invoice.booking.referenceNumber}
                  </Typography>
                </div>
                <div className="margin-bottom">
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {`${getLocalizedBookingSyntex(country)} ${t("root:period")}:`}
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {
                      getLocalizedDateFormat(country, invoice.booking.pickupDateTime, DATE_TYPE.EXPANDED, invoice.booking.branch?.timeZone)
                    }
                    {" "}
                    -{" "}
                    {invoice.booking.dropoffDateTime &&
                      getLocalizedDateFormat(country, invoice.booking.dropoffDateTime, DATE_TYPE.EXPANDED, invoice.booking.branch?.timeZone)
                    }
                  </Typography>
                </div>
                {!hideDetails && ([BookingStatus.IN_PROGRESS, BookingStatus.COMPLETED].includes(invoice.booking.status)) &&
                  <div className="margin-bottom">
                    <Typography
                      variant="body1"
                      className="opacity60 padding-bottom--small"
                    >
                      {t("root:vehicles")}:
                    </Typography>
                    <Typography variant="body1" className="semi-bold">
                      {registrationNumbers.length > 0
                        ? registrationNumbers.join(", ").toUpperCase()
                        : ""}
                    </Typography>
                  </div>
                }
                <div>
                  {isEditablePONumber ? (<Grid item xs={10} container className={"po-number-main"}>
                    <TextField
                      label={t("root:poNumber")}
                      id="po-number"
                      fullWidth
                      value={poNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setPoNumber(e.target.value)
                      }}
                      InputProps={{
                        endAdornment: (
                          <>
                            {
                              <Tooltip title={t("misc:save")} style={{ padding: 0 }}>
                                <IconButton
                                  aria-label="toggle po number "
                                  onClick={() => {
                                    addPoNumberToBooking(poNumber)
                                    setIsEditablePONumber(false)
                                  }}
                                >
                                  <DoneIcon />
                                </IconButton>
                              </Tooltip>
                            }
                            <Tooltip title={t("misc:close")} style={{ padding: 0 }}>
                              <IconButton
                                aria-label="toggle po number"
                                onClick={() => {
                                  setPoNumber(invoice?.booking?.poNumber || "")
                                  setIsEditablePONumber(false)
                                }}
                              >
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )
                      }}
                    />
                  </Grid>) : (<div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography
                      variant="body1"
                      className="opacity60 padding-bottom--small"
                    >
                      {t("root:poNumber")}:
                    </Typography>
                    <Typography variant="body1" className="semi-bold">
                      {poNumber || t("root:na")}
                    </Typography>
                    {invoice.status === InvoiceStatus.UNPAID && (<Grid >
                      <Tooltip title={poNumber ? t("misc:edit") : t("misc:clickToAdd")} style={{ padding: 0 }}>
                        <IconButton
                          aria-label="toggle edit visibility"
                          onClick={() => setIsEditablePONumber(true)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>)}
                  </div>)}
                </div>
              </>
            )}
            {invoice.consolidatedBy && (
              <div className="margin-bottom">
                <Typography
                  variant="body1"
                  className="opacity60 padding-bottom--small"
                >
                  {`${t("root:invoiceVoidedBy")}:`}
                </Typography>
                <Typography
                  variant="body1"
                  className={`${styles.consolidatedInvoiceRow} semi-bold`}
                  onClick={() => history.push(`/account/invoices/${invoice.consolidatedBy?.id}`)}
                >
                  {invoice.consolidatedBy.invoiceRef}
                </Typography>
              </div>
            )}
          </div>
        </Grid>
        <Grid item xs={12} md={5} style={{ display: "flex" }}>
          <div className={styles.card} style={{ marginRight: 0 }}>
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:invoicePeriod")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {getLocalizedDateFormat(country, invoice.startDate, DATE_TYPE.EXPANDED, invoice.booking?.branch?.timeZone)}{" "}
                - {getLocalizedDateFormat(country, invoice.endDate, DATE_TYPE.EXPANDED, invoice.booking?.branch?.timeZone)}
              </Typography>
            </div>
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:invoiceStatus")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                <span
                  style={{
                    padding: "0 5px",
                    borderRadius: 3,
                    color: ["PARTIALLY PAID", "UNPAID"].includes(invoice.status)
                      ? "#be0000"
                      : "#005b41",
                    backgroundColor: ["PARTIALLY PAID", "UNPAID"].includes(
                      invoice.status
                    )
                      ? "#e5c7c7"
                      : "#c7e5dc",
                  }}
                >
                  {["PARTIALLY PAID", "UNPAID"].includes(invoice.status)
                    ? t("root:unpaid")
                    : invoice.status}
                </span>
              </Typography>
            </div>
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:invoiceAmount")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {t("root:total")}:{" "}
                {toCurrency(invoice.totalPayableAmount, currency, locale)}
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {t("root:amountDue")}: {toCurrency(invoice.dueAmount, currency, locale)}
              </Typography>
            </div>
            <div className="margin-bottom">
              <Typography
                variant="body1"
                className="opacity60 padding-bottom--small"
              >
                {t("root:generatedAt")}:
              </Typography>
              <Typography variant="body1" className="semi-bold">
                {getLocalizedDateFormat(country, (invoice.confirmedAt) ? invoice.confirmedAt : invoice.dateCreated, DATE_TYPE.EXPANDED, invoice.booking?.branch?.timeZone)}
              </Typography>
            </div>
            {invoice.invoiceType !== InvoiceType.CONSOLIDATED &&
              <div>
                <Typography
                  variant="body1"
                  className="opacity60 padding-bottom--small"
                >
                  {t("root:dueDate")}:
                </Typography>
                <Typography variant="body1" className="semi-bold">
                  {getLocalizedDateFormat(country, invoice.dueDate, DATE_TYPE.EXPANDED, invoice.booking?.branch?.timeZone)}
                </Typography>
              </div>
            }
          </div>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <div className={`${styles.box}`}>
            <div style={{ margin: "0 -10px -10px -10px" }}>
              <TableContainer style={{ maxHeight: "100%" }}>
                <Table stickyHeader aria-label="customized table">
                  <TableHead>
                    <StyledTableRow>
                      {columns.map((column: string) => {
                        return <StyledTableCell>{column === "VAT/TAX" ? getLocalizedTaxSyntex(country) : t<string>(`misc:columnKey.${column}`)}</StyledTableCell>;
                      })}
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.invoiceItems?.map(
                      (item: IInvoiceItem, idx: number) => {
                        let taxAmount = 0;
                        if (item.taxAmount) {
                          taxAmount = item.taxAmount;
                        }
                        let vehicleGroupId = "";
                        invoice.booking?.vehicleGroups?.forEach((vg) => {
                          if (item.description && vg.name && item.description.toLowerCase().includes(vg.name.toLowerCase())) {
                            vehicleGroupId = typeof vg.vehicleGroup === 'string' ? vg.vehicleGroup : vg.vehicleGroup.id;
                          }
                        });
                        const applicableTaxes = getVehicleGroupApplicableTaxesDetails(invoice.tax, vehicleGroupId);
                        const rentalTaxDetails = getTaxDescription(applicableTaxes, currency, locale);
                        let rateTypeName = item.rateTypeName ? item.rateTypeName : invoice.booking?.rateTypeName;
                        return (
                          <StyledTableRow>
                            <StyledTableCell>{idx + 1}</StyledTableCell>
                            <StyledTableCell>
                              {item.type === IInvoiceItemType.RENTAL_AMOUNT
                                ? (invoice.booking?.isSubscription ? item.description.toUpperCase() : `${t("root:vehicleRentalCharges")} (${item.description.toUpperCase()})`)
                                : item.type === IInvoiceItemType.DEPOSIT
                                  ? `${t("root:deposit")} (${item.description.toUpperCase()}) (${t("root:refundable")})`
                                  : item.type === IInvoiceItemType.INSURANCE
                                    ? `${t("root:insurance")} (${item.description.toUpperCase()})`
                                    : item.type === IInvoiceItemType.PROTECTION_PLAN
                                      ? `${t("root:protectionPlan")} (${item.description.toUpperCase()})`
                                      : item.type === IInvoiceItemType.LOCATION_SURCHARGE
                                        ? `${t("root:locationSurcharge")} (${item.description.toUpperCase()})`
                                        : item.type === IInvoiceItemType.ADDON
                                          ? `${item.description.toUpperCase()}`
                                          : item.description.toUpperCase()}{" "}
                              <br />
                              {invoice.invoiceType === InvoiceType.SUNDRY ? (
                                <>
                                  {rentalTaxDetails && rentalTaxDetails.length > 0 && rentalTaxDetails.map(title => {
                                    return (
                                      <>
                                        <span>({title})</span><br></br>
                                      </>
                                    )
                                  })}
                                </>
                              ) : (
                                <>
                                  {item.taxDescription &&
                                    item.taxDescription.length > 0 &&
                                    item.taxDescription.map((title) => {
                                      return (
                                        <>
                                          <span>({title})</span>
                                          <br></br>
                                        </>
                                      );
                                    })}
                                </>
                              )}
                            </StyledTableCell>
                            <StyledTableCell>{item.quantity}</StyledTableCell>
                            <StyledTableCell>
                              {toCurrency(item.unitPrice, currency, locale)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {item.duration
                                ? item.isProrated
                                  ? `${item.duration} ${getRateTypeString(
                                    rateTypeName
                                  )} (${item.proratedBillingDays
                                  } ${t("root:daysProRated")})`
                                  : `${item.duration} ${getRateTypeString(
                                    rateTypeName
                                  )}`
                                : t("root:na")}
                            </StyledTableCell>
                            <StyledTableCell>
                              {item.type === IInvoiceItemType.RENTAL_AMOUNT && (invoice.discount?.amount > 0 || invoice.discountAmount > 0) ? 
                                toCurrency(item.totalAmount, currency, locale) : 
                                t("root:na")
                              }
                            </StyledTableCell>
                            <StyledTableCell>
                              {toCurrency(taxAmount, currency, locale)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {toCurrency(
                                (item.totalAmount) + (item.taxAmount ? item.taxAmount : 0),
                                currency,
                                locale
                              )}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      }
                    )}
                    {invoice.discountAmount > 0 && (
                      <StyledTableRow>
                        <StyledTableCell colSpan={4}></StyledTableCell>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body1">{"Discount"}</Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          {"-"}
                          {toCurrency(invoice.discountAmount, currency, locale)}
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                    {invoice.offerDiscountAmount > 0 && (
                      <StyledTableRow>
                        <StyledTableCell colSpan={4}></StyledTableCell>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body1">
                            {t("root:couponDiscount")}
                            {invoice.appliedOffers && invoice.appliedOffers.length > 0
                              ? ` (${invoice.appliedOffers.map((appliedOffer) => appliedOffer.code || appliedOffer.offerName).join(", ")})`
                              : ""}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          {"-"}
                          {toCurrency(invoice.offerDiscountAmount, currency, locale)}
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                    <StyledTableRow>
                      <StyledTableCell colSpan={4}></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body1">{t("root:subTotal")}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {
                          toCurrency(
                            (invoice.invoiceItems || []).reduce((sum, item) => sum + (item.totalAmount || 0), 0),
                            currency,
                            locale
                          )
                        }
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell colSpan={4}></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body1">{`${t("root:total")} ${getLocalizedTaxSyntex(country)}`}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {toCurrency(
                          invoice.taxAmount,
                          currency,
                          locale
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell colSpan={4}></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body1">{t("root:total")}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {toCurrency(
                          invoice.totalPayableAmount,
                          currency,
                          locale
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell colSpan={4}></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body1">{t("root:paid")}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {toCurrency(
                          invoice.confirmedPaidAmount,
                          currency,
                          locale
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell colSpan={2}>
                        {invoice.booking?.taxExempted ?
                          <Typography variant="h4">{`*${t("root:thisIsA")} ${getLocalizedTaxSyntex(country)} ${t("root:exempted")} ${getLocalizedBookingSyntex(country).toLowerCase()}.`}</Typography> :
                          <Typography></Typography>
                        }
                        {invoice.invoiceType !== InvoiceType.SUNDRY && invoice.booking?.zeroCostBooking ?
                          <Typography variant="h4">*{t("root:thisIsTestCourtsey")} {getLocalizedBookingSyntex(country).toLowerCase()}.</Typography> :
                          <Typography></Typography>
                        }
                      </StyledTableCell>
                      <StyledTableCell colSpan={2}></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="h4">{t("root:balanceDue")}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {toCurrency(invoice.dueAmount, currency, locale)}
                      </StyledTableCell>
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </Grid>
        {invoice.invoiceType !== InvoiceType.SUNDRY && invoice.booking?.zeroCostBookingNote && (
          <Grid item xs={12}>
            <Typography variant={"h3"}>
              {t(`root:additionalNote`)}:
            </Typography>
            <span style={{ fontSize: 14, fontFamily: '"Amazon Ember", "Helvetica Neue Light", "Helvetica" , "Arial", "sans-serif"' }}>{invoice.booking.zeroCostBookingNote}</span>
          </Grid>
        )}
        {invoice.invoiceType === InvoiceType.CONSOLIDATED && (
          <Grid item xs={12}>
            <div className={`${styles.box}`}>
              <div className="font-medium semi-bold margin-bottom padding-bottom">
                {t("root:invoicesConsolidated")}
              </div>
              <div style={{ margin: "0 -10px -10px -10px" }}>
                <TableContainer style={{ maxHeight: "100%" }}>
                  <Table stickyHeader aria-label="customized table">
                    <TableHead>
                      <StyledTableRow>
                        {consolidatedInvoicesColumns.map((column: string) => {
                          return <StyledTableCell>{t<string>(`misc:consolidatedInvoicesColumnsKey.${column}`)}</StyledTableCell>;
                        })}
                      </StyledTableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.invoices && invoice.invoices.map((item, index) => {
                        return (
                          <StyledTableRow key={index} onClick={() => history.push(`/account/invoices/${item.id}`)}>
                            <StyledTableCell>
                              {index + 1}
                            </StyledTableCell>
                            <StyledTableCell className={styles.consolidatedInvoiceRow}>
                              {item.invoiceRef}
                            </StyledTableCell>
                            <StyledTableCell className={styles.consolidatedInvoiceRow}>
                              {item.vrns?.join(', ')?.toUpperCase() || '-'}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>
          </Grid>
        )}
        {invoice.payments.length > 0 &&
          invoice.payments.some((item) => item.amount > 0) && (
            <Grid item xs={12}>
              <div className={`${styles.box}`}>
                <div className="font-medium semi-bold margin-bottom padding-bottom">
                  {t("root:payments")}
                </div>
                <div style={{ margin: "0 -10px -10px -10px" }}>
                  <TableContainer style={{ maxHeight: "100%" }}>
                    <Table stickyHeader aria-label="customized table">
                      <TableHead>
                        <StyledTableRow>
                          {paymentColumns.map((column: string) => {
                            return <StyledTableCell>{t<string>(`misc:paymentColumnsKey.${column}`)}</StyledTableCell>;
                          })}
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {invoice.payments &&
                          invoice.payments
                            .filter((item) => item.amount > 0)
                            .map((item, index) => {
                              return (
                                <StyledTableRow key={index}>
                                  <StyledTableCell>
                                    {getLocalizedDateFormat(country, item.dateCreated, DATE_TYPE.CONDENSED, invoice.booking?.branch?.timeZone)}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {toCurrency(item.amount, currency, locale)}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {item.paymentMode} ({PaymentGatewayLabels[item?.paymentGateway?? "OFFLINE"]})
                                  </StyledTableCell>
                                  <StyledTableCell>{item.status}</StyledTableCell>
                                </StyledTableRow>
                              );
                            })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </Grid>)}
        {invoice?.adjustedCreditNotes && invoice.adjustedCreditNotes.length > 0 &&
          invoice.adjustedCreditNotes.some((item) => item.amount > 0) && (
            <Grid item xs={12}>
              <div className={`${styles.box}`}>
                <div className="font-medium semi-bold margin-bottom padding-bottom">
                  Credits
                </div>
                <div style={{ margin: "0 -10px -10px -10px" }}>
                  <TableContainer style={{ maxHeight: "100%" }}>
                    <Table stickyHeader aria-label="customized table">
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableCell>{t("root:date")}</StyledTableCell>
                          <StyledTableCell>{t("root:amount")}</StyledTableCell>
                          <StyledTableCell>{t("root:creditNoteId")}</StyledTableCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {invoice.adjustedCreditNotes &&
                          invoice.adjustedCreditNotes
                            .filter((item) => item.amount > 0)
                            .map((item, index) => {
                              return (
                                <StyledTableRow key={index} onClick={() => history.push(`/account/credit-notes/${item.creditNote.id}`)}>
                                  <StyledTableCell>
                                    {getLocalizedDateFormat(country, item.adjustedAt, DATE_TYPE.CONDENSED, invoice.booking.branch?.timeZone)}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {toCurrency(item.amount, currency, locale)}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <span
                                      style={{
                                        color: "rgb(24, 117, 210)",
                                        textDecoration: "none",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {item.creditNote.creditNoteRef}
                                    </span>
                                  </StyledTableCell>
                                </StyledTableRow>
                              );
                            })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </Grid>)}
      </Grid>
      <Grid container></Grid>
    </div>
  );
};

export default Invoice;
